import { useEffect, useMemo, useState } from "react";
import { X, Play } from "lucide-react";
import Photo360Viewer from "@/components/Photo360Viewer";
import SmartMedia from "@/components/SmartMedia";
import RichText from "@/components/RichText";
import { mediaProxyUrlForced, type MediaRef } from "@/lib/media";

/**
 * Einzelne „Wann HypeFly"-Kachel mit:
 * - Vorschau-Bild (aus CMS `tile.image`)
 * - Titel + Body
 * - „Foto ansehen"-Button (immer sichtbar → Photo-Lightbox)
 * - „360°-Beispiel öffnen"-Button (nur wenn `tile.skybox` gesetzt → Photo360Viewer)
 *
 * Kein Slug/Projekt-Fetch mehr — reine CMS-Daten. Fehlt `skybox`, wird der
 * 360°-Button nicht gerendert (kein Fake-Fallback).
 */
type Props = {
    title: string;
    body?: string;
    image?: MediaRef;
    skybox?: MediaRef;
    alt?: string;
};

type MediaTypeStr = "photo" | "photo_360" | "video" | "video_360" | undefined;

const EYEBROW_LABEL: Record<Exclude<MediaTypeStr, undefined>, string> = {
    photo: "Foto",
    photo_360: "360°-Foto",
    video: "Video",
    video_360: "360°-Video",
};

export default function UseCaseTile({ title, body, image, skybox, alt }: Props) {
    const [modal, setModal] = useState<"photo" | "sky" | null>(null);
    // media_type wird via /info geholt und cached (Modul-scope-Cache in
    // SmartMedia bereits vorhanden). Wir brauchen ihn hier für:
    //  1) Play-Overlay auf der Kachel bei Video/Video_360.
    //  2) Dynamischen Eyebrow im Modal-Header („360°-VIDEO" statt „FOTO").
    const [mediaType, setMediaType] = useState<MediaTypeStr>(undefined);
    const imageMediaId = useMemo<string | undefined>(() => {
        if (!image) return undefined;
        if (typeof image === "string") return image;
        return image.media_id || undefined;
    }, [image]);

    useEffect(() => {
        if (!imageMediaId) {
            setMediaType(undefined);
            return;
        }
        let cancelled = false;
        const base = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${base}/api/media/${imageMediaId}/info`, { credentials: "omit" })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (cancelled) return;
                const t = d?.media_type as MediaTypeStr;
                setMediaType(t);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [imageMediaId]);

    const isVideo = mediaType === "video" || mediaType === "video_360";
    const hasMedia = Boolean(image);
    // media_id extrahieren, damit wir für den 360°-Viewer garantiert den
    // Same-Origin-Proxy nutzen (nicht die direkte R2-URL aus `path`).
    const skyId: string | undefined =
        typeof skybox === "string"
            ? skybox
            : skybox?.media_id || undefined;
    const hasSky = Boolean(
        skybox &&
        (typeof skybox === "string" ? skybox : skybox.media_id || skybox.path),
    );

    // ESC schließt Modal
    useEffect(() => {
        if (!modal) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setModal(null);
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [modal]);

    return (
        <>
            <article
                data-testid={`use-case-${title}`}
                className="group overflow-hidden rounded-sm transition-transform hover:-translate-y-0.5"
                style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
            >
                <div
                    className="relative w-full aspect-[3/2] overflow-hidden"
                    style={{ background: "hsl(var(--hf-surface))" }}
                >
                    {hasMedia ? (
                        <>
                            <SmartMedia
                                mediaRef={image}
                                alt={alt || title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                videoAutoplay={false}
                                videoLoop={false}
                                videoMuted={true}
                                interactive360={false}
                                showTypeBadge={false}
                                loading="eager"
                                testId={`use-case-image-${title}`}
                            />
                            {/* Play-Icon-Overlay: signalisiert Video-Content
                                UND ist selbst klickbar → öffnet Modal direkt
                                (User muss nicht zum „Ansehen"-Button unten
                                scrollen). Bei Fotos/360°-Fotos rendern wir das
                                Overlay nicht, damit der klassische Zoom-in-
                                Effekt beim Hover erhalten bleibt. */}
                            {isVideo && (
                                <button
                                    type="button"
                                    onClick={() => setModal("photo")}
                                    className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
                                    aria-label={`${title} — Video abspielen`}
                                    data-testid={`use-case-play-overlay-${title}`}
                                >
                                    <span
                                        className="inline-flex items-center justify-center w-16 h-16 rounded-full transition-transform group-hover:scale-105"
                                        style={{
                                            background: "hsl(var(--hf-gold) / 0.92)",
                                            color: "hsl(var(--hf-ink))",
                                            boxShadow: "0 6px 24px hsl(var(--hf-ink) / 0.35)",
                                        }}
                                        aria-hidden
                                    >
                                        <Play size={26} fill="currentColor" strokeWidth={0} />
                                    </span>
                                </button>
                            )}
                        </>
                    ) : (
                        <div
                            className="w-full h-full flex items-center justify-center"
                            style={{
                                background:
                                    "linear-gradient(135deg, hsl(var(--hf-surface)) 0%, hsl(var(--hf-ink)) 100%)",
                                color: "hsl(var(--hf-gold-soft))",
                            }}
                            data-testid={`use-case-image-placeholder-${title}`}
                            aria-hidden
                        >
                            <span
                                className="text-[10px] tracking-[0.24em] uppercase opacity-80"
                            >
                                Bild folgt
                            </span>
                        </div>
                    )}
                </div>
                <div className="p-5 md:p-6">
                    <h3 className="font-display text-lg leading-snug">{title}</h3>
                    {body && (
                        <div className="mt-1.5 text-sm text-hf-muted">
                            <RichText html={body} />
                        </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                        {hasMedia && (
                            <button
                                type="button"
                                onClick={() => setModal("photo")}
                                className="hf-btn-ghost text-xs uppercase tracking-[0.14em] px-3 py-2 rounded-sm"
                                style={{ border: "1px solid hsl(var(--hf-line))" }}
                                data-testid="use-case-btn-photo"
                            >
                                Ansehen
                            </button>
                        )}
                        {hasSky && (
                            <button
                                type="button"
                                onClick={() => setModal("sky")}
                                className="text-xs uppercase tracking-[0.14em] px-3 py-2 rounded-sm"
                                style={{
                                    background: "hsl(var(--hf-gold))",
                                    color: "hsl(var(--hf-ink))",
                                }}
                                data-testid="use-case-btn-360"
                            >
                                360°-Beispiel öffnen
                            </button>
                        )}
                    </div>
                </div>
            </article>

            {modal && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                    style={{ background: "hsl(var(--hf-ink) / 0.85)" }}
                    onClick={() => setModal(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    data-testid="use-case-modal"
                >
                    <div
                        className="relative max-w-5xl w-full max-h-[90vh] rounded-sm overflow-hidden"
                        style={{ background: "hsl(var(--hf-bg))" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                            <div>
                                <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                                    {modal === "photo"
                                        ? (mediaType ? EYEBROW_LABEL[mediaType] : "Medium")
                                        : "360°-Foto"}
                                </div>
                                <div className="font-display text-lg">{title}</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setModal(null)}
                                className="p-2 rounded-sm hover:bg-hf-surface"
                                aria-label="Schließen"
                                data-testid="use-case-modal-close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="w-full flex items-center justify-center" style={{ background: "hsl(var(--hf-surface))" }}>
                            {modal === "photo" && hasMedia && (
                                // aspect-video Wrapper: erlaubt Video360Viewer & Photo360Viewer
                                // ausreichend Höhe (WebGL-Canvas ist nicht per h-auto berechenbar).
                                // Für flache Photos/Videos rendert SmartMedia ein <img>/<video>
                                // mit `object-contain` in der gleichen Fläche → keine Verzerrung.
                                //
                                // WICHTIG: `interactive360=true` + `autoPlay360=true` sorgt dafür,
                                // dass 360°-Videos im Three.js-Sphere-Viewer starten (nicht als
                                // flaches Fisheye-Video) und WebGL sofort initialisiert wird.
                                <div className="w-full aspect-video max-h-[75vh]">
                                    <SmartMedia
                                        mediaRef={image}
                                        alt={alt || title}
                                        className="w-full h-full object-contain"
                                        videoAutoplay={true}
                                        videoLoop={true}
                                        videoMuted={true}
                                        videoControls={true}
                                        interactive360={true}
                                        autoTour360={true}
                                        autoPlay360={true}
                                        showTypeBadge={false}
                                        loading="eager"
                                        testId="use-case-modal-media"
                                    />
                                </div>
                            )}
                            {modal === "sky" && hasSky && skyId && (
                                <div className="w-full aspect-video max-h-[75vh]">
                                    <Photo360Viewer
                                        src={mediaProxyUrlForced(skyId)}
                                        testIdPrefix="use-case-photo360"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
